import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceNavTabs — a module's tab strip of links (T-0185).
 *
 * Mirrors `.tabs` + `.rq-tabs` in `docs/requirement-library/prototype/`:
 * 13px tabs 24px apart, the active one ink-900 / 600 with the amber
 * underline, an optional count pill and an optional indicator (for
 * example the amber "waiting for review" dot).
 *
 * The tabs move between a module's screens, so they are links in a
 * labelled `nav` (`aria-current="page"` on the active one), not an ARIA
 * tablist. Pass `asChild` to render the router's link:
 *
 *   <WorkspaceNavTabs aria-label="Discovery">
 *     <WorkspaceNavTab asChild active count={12}><Link to=…>Sessions</Link></WorkspaceNavTab>
 *   </WorkspaceNavTabs>
 */

export function WorkspaceNavTabs({
  'aria-label': ariaLabel,
  children,
  className,
}: {
  'aria-label': string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <nav aria-label={ariaLabel} className={cn('flex flex-wrap gap-6 px-6', className)}>
      {children}
    </nav>
  );
}

export interface WorkspaceNavTabProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  /** Count pill after the label; omitted when null or undefined. */
  count?: number | null;
  /** Extra marker after the count, such as a status dot. */
  indicator?: React.ReactNode;
  asChild?: boolean;
}

export const WorkspaceNavTab = React.forwardRef<HTMLAnchorElement, WorkspaceNavTabProps>(
  ({ active = false, count, indicator, asChild = false, className, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        ref={ref}
        aria-current={active ? 'page' : undefined}
        className={cn(
          '-mb-px inline-flex items-center gap-2 border-b-2 pb-2.5 pt-2 text-body-sm no-underline',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          active
            ? 'border-accent-amber-600 font-semibold text-ink-900'
            : 'border-transparent font-medium text-ink-500 hover:text-ink-700',
          className,
        )}
        {...props}
      >
        <Slottable>{children}</Slottable>
        {count != null ? (
          <span className="rounded-full bg-ink-100 px-1.5 py-px text-[11px] font-medium text-ink-500">
            {count.toLocaleString()}
          </span>
        ) : null}
        {indicator}
      </Comp>
    );
  },
);
WorkspaceNavTab.displayName = 'WorkspaceNavTab';
